'use client';

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, CreditCard, ShieldCheck, Store } from 'lucide-react';
import { RegisterStepper } from '@/components/register/RegisterStepper';
import { StepPersonalInfo, type PersonalInfoData } from '@/components/register/StepPersonalInfo';
import { StepBusinessInfo, type BusinessInfoData } from '@/components/register/StepBusinessInfo';
import { StepComplete } from '@/components/register/StepComplete';
import { register as registerApi } from '@/lib/api/auth.api';
import { bootstrapAuth } from '@/lib/api/auth.api';
import type { AxiosError } from 'axios';

export default function RegisterPage() {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [personalData, setPersonalData] = useState<PersonalInfoData | null>(null);
  const [businessName, setBusinessName] = useState('');

  const registerMutation = useMutation({
    mutationFn: async (bizData: BusinessInfoData) => {
      if (!personalData) throw new Error('Missing personal info');
      return registerApi({
        name:                  personalData.name,
        email:                 personalData.email,
        phone:                 personalData.phone,
        password:              personalData.password,
        password_confirmation: personalData.password_confirmation,
        business_name:         bizData.business_name,
        business_type:         bizData.business_type,
        business_category_id:  bizData.business_category_id,
        business_phone:        bizData.business_phone,
        business_email:        bizData.business_email || undefined,
      });
    },
    onSuccess: async (res) => {
      setBusinessName(res.data.business.name);
      await bootstrapAuth(res.data.token);
      setStep(3);
    },
    onError: (error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>) => {
      const errors = error.response?.data?.errors;
      if (errors) {
        const first = Object.values(errors)[0]?.[0];
        toast.error(first ?? error.response?.data?.message ?? 'Registration failed');
      } else {
        toast.error(error.response?.data?.message ?? 'Registration failed');
      }
    },
  });

  const handlePersonalNext = (data: PersonalInfoData) => {
    setPersonalData(data);
    setStep(2);
  };

  const handleBusinessNext = (data: BusinessInfoData) => {
    registerMutation.mutate(data);
  };

  return (
      <div className="min-h-screen bg-slate-50 text-slate-950">
        <div className="grid min-h-screen lg:grid-cols-[420px_1fr] xl:grid-cols-[480px_1fr]">

          {/* ── Left panel ── */}
          <section className="relative hidden overflow-hidden bg-[#13092e] lg:flex lg:flex-col">
            {/* Subtle ambient glow */}
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_20%_10%,rgba(167,139,250,0.35),transparent_55%),radial-gradient(ellipse_at_80%_90%,rgba(99,102,241,0.2),transparent_50%)]" />

            <div className="relative z-10 flex flex-1 flex-col px-10 py-10">
              {/* Logo */}
              <div className="inline-flex items-center gap-3 self-start">
                <img
                  src="/logo-u.png"
                  alt="UniPOS"
                  className="h-8 w-auto object-contain shrink-0"
                />
                <div className="flex flex-col">
                  <span className="font-mono text-xl font-bold tracking-tight text-white leading-none">
                    Uni<span
                      className="bg-gradient-to-r from-[#123D83] via-[#087DC1] to-[#08AFCB] bg-clip-text text-transparent"
                      style={{
                        backgroundImage: 'linear-gradient(to right, #123D83 0%, #087DC1 50%, #08AFCB 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      POS
                    </span>
                  </span>
                  <span className="text-[7.5px] font-semibold tracking-[0.16em] text-white uppercase mt-0.5">
                    ONE POS FOR EVERY BUSINESS
                  </span>
                </div>
              </div>

              {/* Hero copy */}
              <div className="mt-16">
                <p className="text-xs font-semibold uppercase tracking-widest text-violet-400">
                  Merchant onboarding
                </p>
                <h1 className="mt-4 text-[2.25rem] font-semibold leading-[1.18] tracking-tight text-white">
                  The workspace your store runs on.
                </h1>
                <p className="mt-5 text-[15px] leading-7 text-white/60">
                  One account gives you a business profile, POS tools, staff management, and a live customer directory — all ready on day one.
                </p>
              </div>

              {/* Feature list */}
              <ul className="mt-10 space-y-3">
                {[
                  { icon: Store,       text: 'Business profile created during signup' },
                  { icon: CreditCard,  text: 'POS-ready sales and customer foundation' },
                  { icon: ShieldCheck, text: 'Secure owner account with dashboard access' },
                ].map((item) => (
                    <li
                        key={item.text}
                        className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.05] px-4 py-3"
                    >
                      <item.icon className="h-[18px] w-[18px] shrink-0 text-violet-300" />
                      <span className="text-sm text-white/80">{item.text}</span>
                    </li>
                ))}
              </ul>

              {/* Progress indicator */}
              <div className="mt-auto">
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                  <p className="text-xs font-semibold uppercase tracking-widest text-violet-400">
                    Setup checklist
                  </p>
                  <ul className="mt-4 space-y-2.5">
                    {[
                      { label: 'Create your owner account', done: step > 1 },
                      { label: 'Register your business',    done: step > 2 },
                      { label: 'Access your dashboard',     done: step === 3 },
                    ].map((item) => (
                        <li key={item.label} className="flex items-center gap-2.5">
                          <CheckCircle2
                              className={`h-4 w-4 shrink-0 transition-colors ${
                                  item.done ? 'text-emerald-400' : 'text-white/20'
                              }`}
                          />
                          <span className={`text-sm transition-colors ${item.done ? 'text-white/90' : 'text-white/40'}`}>
                        {item.label}
                      </span>
                        </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* ── Right panel ── */}
          <section className="flex min-h-screen items-start justify-center px-4 py-10 sm:px-8 lg:items-center lg:py-16">
            <div className="w-full max-w-[600px]">

              {/* Mobile logo */}
              <div className="mb-8 inline-flex items-center gap-3 lg:hidden">
                <img
                  src="/logo-u.png"
                  alt="UniPOS"
                  className="h-8 w-auto object-contain shrink-0"
                />
                <div className="flex flex-col">
                  <span className="font-mono text-xl font-bold tracking-tight text-[#17233B] leading-none">
                    Uni<span
                      className="bg-gradient-to-r from-[#123D83] via-[#087DC1] to-[#08AFCB] bg-clip-text text-transparent"
                      style={{
                        backgroundImage: 'linear-gradient(to right, #123D83 0%, #087DC1 50%, #08AFCB 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      POS
                    </span>
                  </span>
                  <span className="text-[7.5px] font-semibold tracking-[0.16em] text-slate-500 uppercase mt-0.5">
                    ONE POS FOR EVERY BUSINESS
                  </span>
                </div>
              </div>

              {/* Step header */}
              <div className="mb-7">
                <p className="text-sm font-semibold text-violet-700">
                  {step === 1 && 'Step 1 of 2'}
                  {step === 2 && 'Step 2 of 2'}
                  {step === 3 && 'All done'}
                </p>
                <h2 className="mt-1.5 text-3xl font-semibold tracking-tight text-slate-950">
                  {step === 1 && 'Tell us about you'}
                  {step === 2 && 'Set up your business'}
                  {step === 3 && 'Workspace ready'}
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  {step === 1 && 'Your details secure the merchant account.'}
                  {step === 2 && 'This creates your first business profile.'}
                  {step === 3 && 'Your dashboard is being prepared.'}
                </p>
              </div>

              <RegisterStepper currentStep={step} />

              {/* Form card */}
              <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                {step === 1 && (
                    <StepPersonalInfo defaultValues={personalData ?? undefined} onNext={handlePersonalNext} />
                )}
                {step === 2 && (
                    <StepBusinessInfo
                        onNext={handleBusinessNext}
                        onBack={() => setStep(1)}
                        isLoading={registerMutation.isPending}
                    />
                )}
                {step === 3 && <StepComplete businessName={businessName} />}
              </div>

              <p className="mt-5 text-center text-sm text-slate-500">
                Already have an account?{' '}
                <a href="/login" className="font-semibold text-violet-700 underline underline-offset-4 hover:text-violet-800">
                  Sign in
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
  );
}